import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Search, ArrowUpDown, ChevronDown, ChevronUp, Mail, XCircle,
  ShoppingCart, TrendingUp, AlertTriangle, DollarSign,
} from 'lucide-react';
import api from '@/lib/api';
import { formatCurrency, formatDate } from '@/lib/utils';
import Input from '@/components/ui/Input';
import Badge from '@/components/ui/Badge';
import Card from '@/components/ui/Card';
import Pagination from '@/components/ui/Pagination';
import LoadingSpinner from '@/components/ui/LoadingSpinner';

interface CartItem {
  id: string;
  quantity: number;
  variant_id: string;
  sku: string;
  price: number;
  variant_name: string;
  product_id: string;
  product_name: string;
  slug: string;
  image_url: string | null;
}

interface LiveCart {
  id: string;
  user_id: string | null;
  session_id: string | null;
  email: string | null;
  coupon_code: string | null;
  item_count: number;
  cart_value: number;
  created_at: string;
  updated_at: string;
  type: 'live';
}

interface AbandonedCart {
  id: string;
  cart_id: string;
  user_id: string | null;
  email: string | null;
  status: AbandonedStatus;
  item_count: number;
  cart_value: number;
  last_activity_at: string;
  email_1_sent_at: string | null;
  email_2_sent_at: string | null;
  email_3_sent_at: string | null;
  recovered_at: string | null;
  recovered_order_id: string | null;
  recovered_revenue: number | null;
  recovered_order_number: string | null;
  created_at: string;
  updated_at: string;
  type: 'abandoned';
}

type AbandonedStatus = 'abandoned' | 'email_1_sent' | 'email_2_sent' | 'email_3_sent' | 'recovered' | 'expired';

interface Stats {
  live_carts: number;
  live_cart_value: number;
  abandoned_total: number;
  abandoned_pending: number;
  recovered: number;
  recovery_rate: number;
  revenue_recovered: number;
  emails_sent: number;
}

interface CartsResponse {
  carts: (LiveCart | AbandonedCart)[];
  pagination: {
    page: number;
    total_pages: number;
    total: number;
    limit: number;
  };
}

type Tab = 'live' | 'abandoned';
type SortField = 'created_at' | 'updated_at' | 'cart_value' | 'last_activity_at';
type SortOrder = 'asc' | 'desc';

const STATUS_OPTIONS: { label: string; value: string }[] = [
  { label: 'All Statuses', value: '' },
  { label: 'Abandoned', value: 'abandoned' },
  { label: 'Email 1 Sent', value: 'email_1_sent' },
  { label: 'Email 2 Sent', value: 'email_2_sent' },
  { label: 'Email 3 Sent', value: 'email_3_sent' },
  { label: 'Recovered', value: 'recovered' },
  { label: 'Expired', value: 'expired' },
];

const getStatusBadge = (status: AbandonedStatus): { variant: 'green' | 'yellow' | 'blue' | 'gray'; label: string } => {
  switch (status) {
    case 'abandoned': return { variant: 'yellow', label: 'Abandoned' };
    case 'email_1_sent': return { variant: 'blue', label: 'Email 1 Sent' };
    case 'email_2_sent': return { variant: 'blue', label: 'Email 2 Sent' };
    case 'email_3_sent': return { variant: 'blue', label: 'Email 3 Sent' };
    case 'recovered': return { variant: 'green', label: 'Recovered' };
    case 'expired': return { variant: 'gray', label: 'Expired' };
    default: return { variant: 'gray', label: status };
  }
};

function getNextEmailNumber(status: AbandonedStatus): 1 | 2 | 3 | null {
  switch (status) {
    case 'abandoned': return 1;
    case 'email_1_sent': return 2;
    case 'email_2_sent': return 3;
    default: return null;
  }
}

export default function Carts() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [carts, setCarts] = useState<(LiveCart | AbandonedCart)[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [pagination, setPagination] = useState({ page: 1, total_pages: 1, total: 0, limit: 25 });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery);
  const [expandedCartId, setExpandedCartId] = useState<string | null>(null);
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const currentTab = (searchParams.get('tab') || 'abandoned') as Tab;
  const currentStatus = searchParams.get('status') || '';
  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const currentSort = (searchParams.get('sort') || 'created_at') as SortField;
  const currentOrder = (searchParams.get('order') || 'desc') as SortOrder;

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    if (debouncedSearch) {
      searchParams.set('q', debouncedSearch);
      searchParams.set('page', '1');
    } else {
      searchParams.delete('q');
    }
    setSearchParams(searchParams, { replace: true });
  }, [debouncedSearch]);

  // Fetch stats
  useEffect(() => {
    api.get<Stats>('/admin/abandoned-carts/stats').then(setStats).catch(() => {});
  }, []);

  // Fetch carts
  useEffect(() => {
    const fetchCarts = async () => {
      setIsLoading(true);
      setError(null);
      setExpandedCartId(null);

      try {
        const params = new URLSearchParams();
        params.set('tab', currentTab);
        params.set('page', currentPage.toString());
        params.set('sort', currentSort);
        params.set('order', currentOrder);
        if (currentStatus) params.set('status', currentStatus);
        if (debouncedSearch) params.set('q', debouncedSearch);

        const response = await api.get<CartsResponse>(`/admin/abandoned-carts?${params.toString()}`);
        setCarts(response.carts);
        setPagination(response.pagination);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load carts');
      } finally {
        setIsLoading(false);
      }
    };

    fetchCarts();
  }, [currentTab, currentPage, debouncedSearch, currentSort, currentOrder, currentStatus]);

  const handleTabChange = (tab: Tab) => {
    searchParams.set('tab', tab);
    searchParams.set('page', '1');
    searchParams.delete('status');
    setSearchParams(searchParams);
  };

  const handlePageChange = (page: number) => {
    searchParams.set('page', page.toString());
    setSearchParams(searchParams);
  };

  const handleSortChange = (field: SortField) => {
    const newOrder = field === currentSort && currentOrder === 'desc' ? 'asc' : 'desc';
    searchParams.set('sort', field);
    searchParams.set('order', newOrder);
    setSearchParams(searchParams);
  };

  const handleStatusFilter = (status: string) => {
    if (status) {
      searchParams.set('status', status);
    } else {
      searchParams.delete('status');
    }
    searchParams.set('page', '1');
    setSearchParams(searchParams);
  };

  const handleExpandRow = async (cartId: string) => {
    if (expandedCartId === cartId) {
      setExpandedCartId(null);
      return;
    }
    setExpandedCartId(cartId);
    setLoadingItems(true);
    try {
      const res = await api.get<{ items: CartItem[] }>(`/admin/abandoned-carts/${cartId}/items`);
      setCartItems(res.items);
    } catch {
      setCartItems([]);
    } finally {
      setLoadingItems(false);
    }
  };

  const handleSendEmail = async (id: string, emailNumber: number) => {
    if (!confirm(`Send recovery email ${emailNumber} to this customer?`)) return;
    setActionLoading(id);
    try {
      await api.post(`/admin/abandoned-carts/${id}/send-email`, { email_number: emailNumber });
      // Refresh stats and list
      api.get<Stats>('/admin/abandoned-carts/stats').then(setStats).catch(() => {});
      const params = new URLSearchParams();
      params.set('tab', currentTab);
      params.set('page', currentPage.toString());
      params.set('sort', currentSort);
      params.set('order', currentOrder);
      if (currentStatus) params.set('status', currentStatus);
      if (debouncedSearch) params.set('q', debouncedSearch);
      const response = await api.get<CartsResponse>(`/admin/abandoned-carts?${params.toString()}`);
      setCarts(response.carts);
      setPagination(response.pagination);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to send email');
    } finally {
      setActionLoading(null);
    }
  };

  const handleMarkExpired = async (id: string) => {
    if (!confirm('Mark this abandoned cart as expired? This cannot be undone.')) return;
    setActionLoading(id);
    try {
      await api.patch(`/admin/abandoned-carts/${id}/status`, { status: 'expired' });
      api.get<Stats>('/admin/abandoned-carts/stats').then(setStats).catch(() => {});
      setCarts((prev) =>
        prev.map((cart) =>
          cart.id === id && cart.type === 'abandoned'
            ? { ...cart, status: 'expired' as AbandonedStatus }
            : cart
        )
      );
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update status');
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Carts</h1>
        <p className="mt-2 text-sm text-gray-600">
          Monitor live shopping carts and manage abandoned cart recovery
        </p>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card padding="md">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-50 rounded-lg">
                <ShoppingCart className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Live Carts</p>
                <p className="text-2xl font-bold text-gray-900">{stats.live_carts}</p>
                <p className="text-xs text-gray-400">{formatCurrency(stats.live_cart_value)} value</p>
              </div>
            </div>
          </Card>
          <Card padding="md">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-50 rounded-lg">
                <AlertTriangle className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Abandoned</p>
                <p className="text-2xl font-bold text-gray-900">{stats.abandoned_pending}</p>
                <p className="text-xs text-gray-400">{stats.abandoned_total} total</p>
              </div>
            </div>
          </Card>
          <Card padding="md">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-50 rounded-lg">
                <TrendingUp className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Recovery Rate</p>
                <p className="text-2xl font-bold text-gray-900">{stats.recovery_rate}%</p>
                <p className="text-xs text-gray-400">{stats.recovered} recovered</p>
              </div>
            </div>
          </Card>
          <Card padding="md">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-50 rounded-lg">
                <DollarSign className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Revenue Recovered</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(stats.revenue_recovered)}</p>
                <p className="text-xs text-gray-400">{stats.emails_sent} emails sent</p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8" aria-label="Cart tabs">
          {([
            { label: 'Abandoned', value: 'abandoned' as Tab },
            { label: 'Live', value: 'live' as Tab },
          ]).map((tab) => (
            <button
              key={tab.value}
              onClick={() => handleTabChange(tab.value)}
              className={`whitespace-nowrap border-b-2 py-4 px-1 text-sm font-medium transition-colors ${
                currentTab === tab.value
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
              }`}
              aria-current={currentTab === tab.value ? 'page' : undefined}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Search and Filters */}
      <Card padding="md">
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <Input
              type="search"
              placeholder="Search by email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              aria-label="Search carts"
            />
          </div>
          <Search className="h-5 w-5 text-gray-400" aria-hidden="true" />
          {currentTab === 'abandoned' && (
            <select
              value={currentStatus}
              onChange={(e) => handleStatusFilter(e.target.value)}
              className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              aria-label="Filter by status"
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          )}
        </div>
      </Card>

      {/* Table */}
      <Card padding="none">
        {error && (
          <div className="px-6 py-4 bg-red-50 border-b border-red-200 text-red-800" role="alert">
            {error}
          </div>
        )}

        {isLoading ? (
          <div className="flex justify-center items-center py-12">
            <LoadingSpinner />
          </div>
        ) : carts.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            {debouncedSearch
              ? 'No carts found matching your search'
              : currentTab === 'live'
                ? 'No active carts in the last 7 days'
                : 'No abandoned carts found'}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="w-8 px-3 py-3" />
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Email
                    </th>
                    {currentTab === 'abandoned' && (
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        Status
                      </th>
                    )}
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                      Items
                    </th>
                    <th scope="col" className="px-6 py-3 text-right">
                      <button
                        onClick={() => handleSortChange('cart_value')}
                        className="group inline-flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-gray-500 hover:text-gray-700"
                      >
                        Value
                        <ArrowUpDown className="h-4 w-4" />
                      </button>
                    </th>
                    {currentTab === 'abandoned' && (
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                        Emails Sent
                      </th>
                    )}
                    <th scope="col" className="px-6 py-3 text-left">
                      <button
                        onClick={() => handleSortChange(currentTab === 'live' ? 'updated_at' : 'last_activity_at')}
                        className="group inline-flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-gray-500 hover:text-gray-700"
                      >
                        Last Activity
                        <ArrowUpDown className="h-4 w-4" />
                      </button>
                    </th>
                    <th scope="col" className="px-6 py-3 text-left">
                      <button
                        onClick={() => handleSortChange('created_at')}
                        className="group inline-flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-gray-500 hover:text-gray-700"
                      >
                        Created
                        <ArrowUpDown className="h-4 w-4" />
                      </button>
                    </th>
                    {currentTab === 'abandoned' && (
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                        Actions
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {carts.map((cart) => {
                    const cartId = cart.type === 'abandoned' ? (cart as AbandonedCart).cart_id : cart.id;
                    const isExpanded = expandedCartId === cartId;

                    return (
                      <CartRow
                        key={cart.id}
                        cart={cart}
                        cartId={cartId}
                        isExpanded={isExpanded}
                        cartItems={cartItems}
                        loadingItems={loadingItems}
                        actionLoading={actionLoading}
                        currentTab={currentTab}
                        onExpandRow={handleExpandRow}
                        onSendEmail={handleSendEmail}
                        onMarkExpired={handleMarkExpired}
                      />
                    );
                  })}
                </tbody>
              </table>
            </div>

            {pagination.total_pages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-700">
                    Showing{' '}
                    <span className="font-medium">
                      {(pagination.page - 1) * pagination.limit + 1}
                    </span>{' '}
                    to{' '}
                    <span className="font-medium">
                      {Math.min(pagination.page * pagination.limit, pagination.total)}
                    </span>{' '}
                    of <span className="font-medium">{pagination.total}</span> results
                  </p>
                  <Pagination
                    currentPage={pagination.page}
                    totalPages={pagination.total_pages}
                    onPageChange={handlePageChange}
                  />
                </div>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}

function CartRow({
  cart,
  cartId,
  isExpanded,
  cartItems,
  loadingItems,
  actionLoading,
  currentTab,
  onExpandRow,
  onSendEmail,
  onMarkExpired,
}: {
  cart: LiveCart | AbandonedCart;
  cartId: string;
  isExpanded: boolean;
  cartItems: CartItem[];
  loadingItems: boolean;
  actionLoading: string | null;
  currentTab: Tab;
  onExpandRow: (id: string) => void;
  onSendEmail: (id: string, emailNumber: number) => void;
  onMarkExpired: (id: string) => void;
}) {
  const isAbandoned = cart.type === 'abandoned';
  const abandonedCart = cart as AbandonedCart;

  const getEmailsSentCount = (): number => {
    if (!isAbandoned) return 0;
    let count = 0;
    if (abandonedCart.email_1_sent_at) count++;
    if (abandonedCart.email_2_sent_at) count++;
    if (abandonedCart.email_3_sent_at) count++;
    return count;
  };

  const nextEmail = isAbandoned ? getNextEmailNumber(abandonedCart.status) : null;
  const canAct = isAbandoned && abandonedCart.status !== 'recovered' && abandonedCart.status !== 'expired';

  return (
    <>
      <tr className="hover:bg-gray-50 transition-colors cursor-pointer" onClick={() => onExpandRow(cartId)}>
        <td className="px-3 py-4">
          {isExpanded
            ? <ChevronUp className="h-4 w-4 text-gray-400" />
            : <ChevronDown className="h-4 w-4 text-gray-400" />
          }
        </td>
        <td className="px-6 py-4">
          <div className="text-sm text-gray-900">
            {cart.email || <span className="text-gray-400 italic">Guest</span>}
          </div>
          {!isAbandoned && (cart as LiveCart).coupon_code && (
            <div className="text-xs text-gray-500">Coupon: {(cart as LiveCart).coupon_code}</div>
          )}
        </td>
        {currentTab === 'abandoned' && (
          <td className="px-6 py-4 whitespace-nowrap">
            <Badge variant={getStatusBadge(abandonedCart.status).variant}>
              {getStatusBadge(abandonedCart.status).label}
            </Badge>
          </td>
        )}
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
          {cart.item_count}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right font-medium">
          {formatCurrency(cart.cart_value)}
        </td>
        {currentTab === 'abandoned' && (
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
            {getEmailsSentCount()} / 3
          </td>
        )}
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
          {formatDate(isAbandoned ? abandonedCart.last_activity_at : cart.updated_at)}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
          {formatDate(cart.created_at)}
        </td>
        {currentTab === 'abandoned' && (
          <td className="px-6 py-4 whitespace-nowrap text-right" onClick={(e) => e.stopPropagation()}>
            {canAct && (
              <div className="flex items-center justify-end gap-2">
                {nextEmail && (
                  <button
                    onClick={() => onSendEmail(cart.id, nextEmail)}
                    disabled={actionLoading === cart.id}
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-700 bg-blue-50 rounded hover:bg-blue-100 disabled:opacity-50"
                    title={`Send email ${nextEmail}`}
                  >
                    <Mail className="h-3.5 w-3.5" />
                    Email {nextEmail}
                  </button>
                )}
                <button
                  onClick={() => onMarkExpired(cart.id)}
                  disabled={actionLoading === cart.id}
                  className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-700 bg-red-50 rounded hover:bg-red-100 disabled:opacity-50"
                  title="Mark as expired"
                >
                  <XCircle className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
            {isAbandoned && abandonedCart.recovered_order_number && (
              <span className="text-xs text-green-600">
                Order #{abandonedCart.recovered_order_number}
              </span>
            )}
          </td>
        )}
      </tr>

      {/* Expanded row - cart items */}
      {isExpanded && (
        <tr>
          <td colSpan={currentTab === 'abandoned' ? 9 : 6} className="px-6 py-4 bg-gray-50">
            {loadingItems ? (
              <div className="flex justify-center py-4">
                <LoadingSpinner />
              </div>
            ) : cartItems.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-2">No items in cart</p>
            ) : (
              <div className="space-y-2">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Cart Items</p>
                {cartItems.map((item) => (
                  <div key={item.id} className="flex items-center gap-4 bg-white rounded-lg p-3 border border-gray-200">
                    {item.image_url ? (
                      <img
                        src={item.image_url}
                        alt={item.product_name}
                        className="w-10 h-10 rounded object-cover"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded bg-gray-100 flex items-center justify-center">
                        <ShoppingCart className="h-4 w-4 text-gray-400" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{item.product_name}</p>
                      {item.variant_name && (
                        <p className="text-xs text-gray-500">{item.variant_name} {item.sku && `(${item.sku})`}</p>
                      )}
                    </div>
                    <div className="text-sm text-gray-500">
                      x{item.quantity}
                    </div>
                    <div className="text-sm font-medium text-gray-900">
                      {formatCurrency(item.price * item.quantity)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}
